import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { NavMenuCategory } from 'modules/products'
import { PATHS } from 'shared/utils/paths'

import { SelectedCategoryHeader } from '../../../types'

import CategoriesNavigationTreeColumn from './CategoriesNavigationTreeColumn'

import styles from './CategoriesNavigationTree.module.scss'

const MAX_DEPTH = 4

// Mocking all products menu item as category is required for proper handling of category tree focus state
export const ALL_PRODUCTS_NAVIGATION_FOCUS_STATE: NavMenuCategory = {
  id: 'all-products',
  parentId: null,
  name: 'Wszystkie produkty',
  totalSlug: PATHS.products,
  depth: 1,
  isVisible: true
}

type CategoriesTreeSelectionStateType = (Partial<NavMenuCategory> | null)[]

const defaultCategoriesTreeSelectionState: CategoriesTreeSelectionStateType =
  Array.from({ length: MAX_DEPTH }, () => null)

interface CategoriesNavigationTreeProps {
  categories: NavMenuCategory[]
  handleClose: () => void
  setSelectedCategoryHeader: (category: SelectedCategoryHeader) => void
}

const CategoriesNavigationTree = ({
  categories,
  setSelectedCategoryHeader
}: CategoriesNavigationTreeProps) => {
  const columnsRef = useRef<HTMLDivElement>(null)
  const [categoriesTreeSelectionState, setCategoriesTreeSelectionState] =
    useState<CategoriesTreeSelectionStateType>(
      defaultCategoriesTreeSelectionState
    )
  const categoriesWithAllProducts = useMemo(
    () => [ALL_PRODUCTS_NAVIGATION_FOCUS_STATE, ...categories],
    [categories]
  )

  const handleCategoryFocus = useCallback(
    (category: NavMenuCategory, columnDepth: number) => {
      setCategoriesTreeSelectionState(prev => {
        // 1. Set current level to  selected category
        const newState = [...prev]

        newState[columnDepth - 1] = category

        // 2. Set deeper levels to null
        newState.fill(null, columnDepth)

        return newState
      })
    },
    []
  )

  const getCategoriesForColumn = useCallback(
    (index: number): NavMenuCategory[] => {
      if (index === 0) return categoriesWithAllProducts

      const parent = categoriesTreeSelectionState[index - 1]

      return parent?.subCategories ?? []
    },
    [categoriesTreeSelectionState, categoriesWithAllProducts]
  )

  const focusCategory = useCallback(
    (category: NavMenuCategory, columnIndex: number) => {
      handleCategoryFocus(category, columnIndex + 1)
      requestAnimationFrame(() => {
        document.getElementById(`category-menu-item-${category.id}`)?.focus()
      })
    },
    [handleCategoryFocus]
  )

  // Update tree header to selected first level category
  useEffect(() => {
    setSelectedCategoryHeader({
      name: categoriesTreeSelectionState[0]?.name || '',
      slug: categoriesTreeSelectionState[0]?.totalSlug || '',
      productsCount: categoriesTreeSelectionState[0]?.productsCount || 0
    })
  }, [categoriesTreeSelectionState, setSelectedCategoryHeader])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, columnIndex: number) => {
      const items = getCategoriesForColumn(columnIndex)

      if (!items.length) return

      const focused = categoriesTreeSelectionState[columnIndex]
      const currentIndex = items.findIndex(item => item.id === focused?.id)

      const next = () =>
        focusCategory(
          items[Math.min(items.length - 1, currentIndex + 1)],
          columnIndex
        )

      const prev = () =>
        focusCategory(items[Math.max(0, currentIndex - 1)], columnIndex)

      const first = () => focusCategory(items[0], columnIndex)
      const last = () => focusCategory(items[items.length - 1], columnIndex)

      const child = () => {
        if (focused?.subCategories?.length) {
          focusCategory(focused.subCategories[0], columnIndex + 1)
        }
      }

      const parent = () => {
        if (columnIndex > 0) {
          const parentCategory = categoriesTreeSelectionState[columnIndex - 1]

          focusCategory(parentCategory as NavMenuCategory, columnIndex - 1)
        }
      }

      const keyMap: Record<string, () => void> = {
        ArrowDown: next,
        ArrowUp: prev,
        ArrowRight: child,
        ArrowLeft: parent,
        Home: first,
        End: last
      }

      const action = keyMap[event.key]

      if (action) {
        event.preventDefault()
        action()
      }
    },
    [categoriesTreeSelectionState, focusCategory, getCategoriesForColumn]
  )

  return (
    <>
      <p id='category-navigation-hint' className='visually-hidden'>
        Użyj strzałek w prawo i w lewo do nawigacji pomiędzy podkategoriami
      </p>
      <nav
        className={styles.columns}
        style={
          {
            '--tree-columns': `266px repeat(${MAX_DEPTH - 1}, 1fr)`
          } as CSSProperties
        }
        ref={columnsRef}
        aria-describedby='category-navigation-hint'
      >
        {categoriesTreeSelectionState.map((_, index) => {
          return (
            <CategoriesNavigationTreeColumn
              key={index}
              index={index}
              isLastColumn={index === MAX_DEPTH - 1}
              categories={categoriesWithAllProducts}
              categoriesTreeSelectionState={categoriesTreeSelectionState}
              handleCategoryFocus={handleCategoryFocus}
              handleKeyDown={handleKeyDown}
            />
          )
        })}
      </nav>
    </>
  )
}

export default CategoriesNavigationTree
